import { useAtom } from '@reatom/npm-react'
import { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { SegmentControl } from '@blade/ui-kit'
import { STATS_PERIODS } from '@root/entities/pair/model/pair-metrics'
import { tableExploreFiltersAtom } from '@root/features/explore'
import { exploreTablePeriodAtom } from '@root/features/explore/atoms/explore-table'
import { mapToQuickBuySection } from '@root/features/explore/atoms/explore-table/reatom-explore-table-params'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'
import { ExploreFilterButton } from '@root/features/explore/ui/explore-filter'
import { EXPLORE_TABLE_HEADER_HEIGHT } from '@root/features/explore/ui/explore-table/explore-table'
import { useExploreTableTab } from '@root/features/explore/ui/hooks/use-explore-tab'
import { QuickBuyInput } from '@root/features/trade/quick-buy/ui'
import { TrendingRankingChips } from '@root/features/trending-pairs'
import { findScrollableAncestor } from '@root/shared/lib/dom/find-scrollable-ancestor'
import { useTranslation } from '@root/shared/localization'
import { type ClassNameProps } from '@root/shared/react'
import {
  AlarmClockIcon,
  FlameIcon,
  PausedBadge,
  TokenAgeIcon,
} from '@root/shared/ui-kit'
import { TOPBAR_HEIGHT } from '@root/shared/ui-kit/utils/page-layout'

import type { PairMetrics } from '@root/entities/pair'
import type {
  ExploreTableBaseModel,
  ExploreTableFeedType,
} from '@root/features/explore/atoms/explore-table'
import type { TransKey } from '@root/shared/localization'

type Props = {
  tableModel: ExploreTableBaseModel
} & ClassNameProps

const PERIOD_LABEL_MAP: Record<PairMetrics.MetricsSpan, TransKey> = {
  '5M': 'common.period.5m',
  '1H': 'common.period.1h',
  '6H': 'common.period.6h',
  '1D': 'common.period.1d',
  ALL: 'common.period.all',
}

export const ExploreTableHeader: React.FC<Props> = ({
  className,
  tableModel,
}) => {
  const { t } = useTranslation()

  const [exploreType, setExploreType] = useExploreTableTab()
  const [explorePeriod, setExplorePeriod] = useAtom(exploreTablePeriodAtom)
  const section = useMemo(
    () => mapToQuickBuySection(exploreType),
    [exploreType],
  )
  const [paused] = useAtom(tableModel.paused)
  const headerRef = useRef<HTMLDivElement>(null)

  const scrollToTop = () => {
    if (!headerRef.current) {
      return
    }

    const scrollParent = findScrollableAncestor(headerRef.current)
    if (!(scrollParent instanceof HTMLElement)) {
      return
    }

    const parentTop = scrollParent.getBoundingClientRect().top
    const headerTop = headerRef.current.getBoundingClientRect().top
    const delta = headerTop - parentTop - TOPBAR_HEIGHT

    if (Math.abs(delta) < 2) {
      return
    }

    scrollParent.scrollTo({
      top: scrollParent.scrollTop + delta,
      behavior: 'smooth',
    })
  }

  const handleTypeChange = (value: ExploreTableFeedType) => {
    scrollToTop()
    setExploreType(value)
  }

  return (
    <Root ref={headerRef} className={className}>
      <HeaderStart>
        <SegmentControl value={exploreType} onChange={handleTypeChange}>
          <TrendingSegmentControlItem
            Icon={FlameIcon}
            value={ExploreFeedType.Trending satisfies ExploreTableFeedType}
          >
            {t('trending')}
          </TrendingSegmentControlItem>
          <CookingSegmentControlItem
            value={ExploreFeedType.Cooking satisfies ExploreTableFeedType}
            Icon={AlarmClockIcon}
          >
            {t('explore.cooking')}
          </CookingSegmentControlItem>
          <NewPairsSegmentControlItem
            Icon={TokenAgeIcon}
            value={ExploreFeedType.New satisfies ExploreTableFeedType}
          >
            {t('new-pairs')}
          </NewPairsSegmentControlItem>
        </SegmentControl>
        {/*
          Beside the tab it belongs to, not in the middle with the period:
          the period is the window every list is read over, this is what
          trending itself means.
        */}
        {exploreType === ExploreFeedType.Trending ? (
          <TrendingRankingChips />
        ) : undefined}
      </HeaderStart>
      <HeaderMiddle>
        <SegmentControl value={explorePeriod} onChange={setExplorePeriod}>
          {STATS_PERIODS.map((period) => (
            <SegmentControl.Item key={period} value={period}>
              {t(PERIOD_LABEL_MAP[period]).toUpperCase()}
            </SegmentControl.Item>
          ))}
        </SegmentControl>
      </HeaderMiddle>
      <HeaderEnd>
        <PausedBadge paused={paused} />
        <ExploreFilterButton size="normal" model={tableExploreFiltersAtom} />
        <QuickBuyInput withPresetSelector section={section} />
      </HeaderEnd>
    </Root>
  )
}

const Root = styled.div`
  position: sticky;
  top: ${TOPBAR_HEIGHT}px;
  z-index: 1;

  display: grid;
  grid-template-columns: 1fr auto 1fr;

  background: var(--background-2);
  border-bottom: 1px solid var(--background-3);
  height: ${EXPLORE_TABLE_HEADER_HEIGHT}px;
  align-items: center;
  padding-inline: 20px 10px;
`

const HeaderStart = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  gap: 20px;
  align-items: center;
`

const HeaderMiddle = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
`

const HeaderEnd = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 6px;
  justify-content: end;
`

const TrendingSegmentControlItem = styled(SegmentControl.Item)`
  &[aria-checked='true'] {
    > svg {
      color: var(--red-9);
    }
  }
`
const NewPairsSegmentControlItem = styled(SegmentControl.Item)`
  &[aria-checked='true'] {
    > svg {
      color: var(--green-9);
    }
  }
`
const CookingSegmentControlItem = styled(SegmentControl.Item)`
  &[aria-checked='true'] {
    > svg {
      color: var(--blue-9);
    }
  }
`
